/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    // Global flag to prevent simultaneous cart updates across all forms
    static cartUpdateInProgress = false;
    static pendingUpdates = [];

    /**
     * Cart state cache: variant_id -> quantity. Initialized from Liquid, updated after add/change.
     */
    static _cartState = null;

    static getCartState() {
      if (ProductForm._cartState === null) {
        ProductForm._cartState = {};
        const items = (typeof theme !== 'undefined' && theme.cartItems) || [];
        items.forEach((item) => {
          const vid = item.variant_id?.toString() || item.variant_id;
          if (vid) {
            ProductForm._cartState[vid] = (ProductForm._cartState[vid] || 0) + (item.quantity || 0);
          }
        });
      }
      return ProductForm._cartState;
    }

    static updateCartState(variantId, quantity) {
      const state = ProductForm.getCartState();
      const vid = String(variantId);
      if (quantity <= 0) {
        delete state[vid];
      } else {
        state[vid] = quantity;
      }
    }

    static resetCartState() {
      ProductForm._cartState = null;
    }

    /**
     * Converts Storefront API cart to Ajax-like format for event.detail.cart compatibility.
     * @param {object} storefrontCart - Storefront API cart (lines.edges, checkoutUrl).
     * @returns {object|null} Cart object with items, item_count, total_price (Ajax format).
     */
    static storefrontCartToAjaxFormat(storefrontCart) {
      if (!storefrontCart?.lines?.edges) return null;
      const edges = storefrontCart.lines.edges;
      const items = edges.map(({ node }, index) => {
        const m = node.merchandise || {};
        const p = m.product || {};
        const price = m.price || {};
        const amount = parseFloat(price.amount || 0);
        const qty = node.quantity || 0;
        const variantId = parseInt((m.id || '').split('/').pop(), 10) || 0;
        const productId = p.id ? parseInt(String(p.id).split('/').pop(), 10) : 0;
        const linePrice = amount * qty;
        return {
          id: node.id,
          key: node.id,
          variant_id: variantId,
          product_id: productId,
          quantity: qty,
          price: Math.round(amount * 100),
          line_price: Math.round(linePrice * 100),
          title: p.title || m.title || '',
          index: index + 1,
        };
      });
      const item_count = items.reduce((s, i) => s + i.quantity, 0);
      const total_price = items.reduce((s, i) => s + i.line_price, 0);
      return {
        items,
        item_count,
        total_price,
        checkoutUrl: storefrontCart.checkoutUrl,
      };
    }

    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            // this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            // this.lineItemChangeListener = this.lineItemChangeListener ||
            //     this.handleLineItemChange.bind(this);
            // document.addEventListener('on:line-item:change', (evt) => {
            //     this.lineItemChangeListener(evt);
            // });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Sets the max attribute of the quantity input
     */
    setMaximumInputQuantity() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            const inventory = inventoryJson.find((item) => item.id === variantId);
            const isBacklogRestricted = productInfo.dataset.backlogRestricted === 'true';

            if (
              isBacklogRestricted &&
              inventory.inventory_management.length !== 0 &&
              inventory.inventory_quantity <= 0
            ) {
              quantityInput.max = 0;
              quantityInput.value = 0;
              this.submitBtn.setAttribute('aria-disabled', 'true');
            } else {
              this.submitBtn.removeAttribute('aria-disabled');

              if (
                inventory.inventory_management.length !== 0 &&
                inventory.inventory_quantity > 0 &&
                (inventory.inventory_policy !== 'continue' || isBacklogRestricted)
              ) {
                quantityInput.max = inventory.inventory_quantity;

                const currentQuantity = Number(quantityInput.value);
                if (currentQuantity > 1 && currentQuantity > inventory.inventory_quantity) {
                  quantityInput.value = quantityInput.max;
                }
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      // minusButton.disabled = newQuantity <= min;
      plusButton.disabled = max > 0 && newQuantity >= max;
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      const value = evt.target.value;

      // If value is empty or invalid, set to minimum (usually 1)
      if (!value || value.trim() === '') {
        const min = parseInt(evt.target.min || '1', 10);
        evt.target.value = min;
        this.updateQuantity(min);
        return;
      }

      this.updateQuantity(value);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const perfStart = performance.now();
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          sections += `,${sectionId}`;
        }
      });

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      };

      try {
        const formVariantId = formData.get('id');
        const requestedQty = parseInt(formData.get('updates[]') || formData.get('quantity') || 1, 10);

        const useStorefrontApi = window.StorefrontCart?.isStorefrontApiEnabled?.();

        let data;
        let newQuantity;
        let existingCartItem;
        let existingQty;

        if (useStorefrontApi) {
          // Storefront API flow - avoids 409 rate limits
          const isNewAdd = !(ProductForm.getCartState?.()[String(formVariantId)] > 0);
          const result = await window.StorefrontCart.addOrUpdateCart(formVariantId, requestedQty, {
            assumeNewAdd: isNewAdd,
          });
          const durationMs = Math.round(performance.now() - perfStart);
          console.log(
            `[CartPerf] addToCartPage variantId=${formVariantId} quantity=${requestedQty} api=storefront durationMs=${durationMs}`,
          );
          if (!result?.cart) throw new Error('Storefront API not configured');

          const cart = result.cart;
          existingQty = result.oldQuantity || 0;
          existingCartItem = existingQty > 0;

          const variantGid = `gid://shopify/ProductVariant/${formVariantId}`;
          const line = cart.lines?.edges?.find((e) => e.node.merchandise?.id === variantGid);
          newQuantity = line ? line.node.quantity : 0;

          const itemCount = cart.lines?.edges?.reduce((sum, e) => sum + (e.node.quantity || 0), 0) || 0;

          data = {
            storefrontCart: cart,
            item_count: itemCount,
          };

          window.StorefrontCart?.updateCartLinks?.();

          // Update button state (Add to cart / Update cart)
          const addText = this.submitBtn.dataset.addToCartText || 'Add to cart';
          if (newQuantity > 0) {
            this.submitBtn.innerHTML = 'Update cart';
            this.submitBtn.classList.remove('btn--primary');
            this.submitBtn.classList.add('btn--third');
          } else {
            this.submitBtn.innerHTML = addText;
            this.submitBtn.classList.remove('btn--third');
            this.submitBtn.classList.add('btn--primary');
          }
        } else {
          // Ajax API flow (original)
          const cartState = ProductForm.getCartState();
          existingQty = cartState[String(formVariantId)] || 0;
          existingCartItem = existingQty > 0;

          let response;

          if (existingCartItem) {
            formData.set('quantity', requestedQty);
            fetchRequestOpts.body = formData;
            if (requestedQty > 0) {
              this.submitBtn.innerHTML = 'Update cart';
              this.submitBtn.classList.remove('btn--primary');
              this.submitBtn.classList.add('btn--third');
            } else {
              this.submitBtn.innerHTML = 'Add to cart';
              this.submitBtn.classList.remove('btn--third');
              this.submitBtn.classList.add('btn--primary');
            }
            response = await fetch(theme.routes.cartChange, fetchRequestOpts);
          } else {
            response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
            this.submitBtn.innerHTML = 'Update cart';
            this.submitBtn.classList.remove('btn--primary');
            this.submitBtn.classList.add('btn--third');
          }

          data = await response.json();
          const durationMs = Math.round(performance.now() - perfStart);
          console.log(
            `[CartPerf] addToCartPage variantId=${formVariantId} quantity=${requestedQty} api=ajax durationMs=${durationMs}`,
          );

          const error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            Object.entries(data.errors).map((item) => item[1].join(', '));
          }
          if (data.status) this.setErrorMsgState(error);
          if (!response.ok) throw new Error(response.status);

          newQuantity = data.items?.[0]?.quantity ?? data.quantity ?? requestedQty;
        }

        if (theme.settings.afterAtc === 'page') {
          // Always redirect to cart page, not checkout (checkoutUrl)
          const url = theme.routes.cart;
          setTimeout(() => {
            window.location.href = url;
          }, 300);
        } else {
          ProductForm.updateCartIcon(data);

          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            if (this.cartDrawer) {
              if (useStorefrontApi && data.storefrontCart) {
                this.cartDrawer.renderFromStorefrontCart?.(
                  data.storefrontCart,
                  !quickAddDrawer &&
                    ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md) ||
                      theme.settings.afterAtc === 'drawer'),
                );
              } else if (data.sections) {
                this.cartDrawer.renderContents(
                  data,
                  !quickAddDrawer &&
                    ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md) ||
                      theme.settings.afterAtc === 'drawer'),
                );
              }
              if (!useStorefrontApi && quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;
                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    data.sections[quickOrderSection.section],
                    quickOrderSection.selector,
                  );
                });
              }
            } else if (!useStorefrontApi && window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        ProductForm.updateCartState(formVariantId, newQuantity);

        const cartForEvent = (useStorefrontApi
          ? ProductForm.storefrontCartToAjaxFormat(data.storefrontCart)
          : data) || { items: [], item_count: 0, total_price: 0 };

        if (existingCartItem) {
          this.dispatchEvent(
            new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: cartForEvent,
                variantId: parseInt(formVariantId),
                oldQuantity: existingQty,
                newQuantity,
              },
            }),
          );
        } else {
          this.dispatchEvent(
            new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: cartForEvent,
                variantId: parseInt(formVariantId),
                newQuantity,
              },
            }),
          );

          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = newQuantity;
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
            this.setMaximumInputQuantity();
          }
        }
      } catch (error) {
        console.log(error);
        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: this.errorMsg.textContent,
            },
          }),
        );

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 900);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * Uses line count (number of distinct line items), not total quantity.
     * @param {object} response - Response JSON (Ajax API) or { storefrontCart } (Storefront API).
     *   - sections['cart-icon-bubble']: server-rendered HTML (preferred for Ajax)
     *   - items: array of line items, items.length = line count (Ajax)
     *   - storefrontCart.lines.edges: edges.length = line count (Storefront API)
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) return;

      // 1. Ajax with sections: server-rendered HTML is authoritative (cart/add.js, cart/change.js)
      if (response.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
        return;
      }

      // 2. Compute line count (number of lines, not total quantity)
      let count = null;
      if (Array.isArray(response.items)) {
        count = response.items.length;
      } else if (response.storefrontCart?.lines?.edges) {
        count = response.storefrontCart.lines.edges.length;
      }

      if (count !== null && count >= 0) {
        if (count > 0) {
          cartIconBubble.innerHTML = `
            <div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">
              ${count < 10000 ? `<span aria-hidden="true" class="header__cart-count-number">${count}</span>` : ''}
              <span class="visually-hidden">${count === 1 ? '1 item in cart' : `${count} items in cart`}</span>
            </div>
          `;
        } else {
          cartIconBubble.innerHTML = '';
        }
      }
    }

    /**
     * Updates the cart icon count in the header (quantity selector flow).
     * Uses line count (number of distinct line items), not total quantity.
     * @param {object} response - Response JSON (Ajax API) or { storefrontCart }.
     */
    static updateCartIcononQtyChange(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) return;

      // 1. Ajax with sections: server-rendered HTML is authoritative
      if (response.sections?.['cart-icon-bubble']) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
        return;
      }

      // 2. Compute line count (number of lines, not total quantity)
      let count = null;
      if (Array.isArray(response.items)) {
        count = response.items.length;
      } else if (response.storefrontCart?.lines?.edges) {
        count = response.storefrontCart.lines.edges.length;
      }

      if (typeof count === 'number' && count >= 0) {
        const visibleCount = cartIconBubble.querySelector('.header__cart-count-number');
        const hiddenCount = cartIconBubble.querySelector('.visually-hidden');
        if (count === 0) {
          cartIconBubble.innerHTML = '';
        } else if (visibleCount && hiddenCount) {
          visibleCount.textContent = count;
          hiddenCount.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
        }
      }
    }

    updateQuantityState(newQuantity) {
      // Validate and ensure newQuantity is a valid number
      const validQuantity = parseInt(newQuantity, 10);

      if (isNaN(validQuantity) || validQuantity < 0) {
        console.error('Invalid quantity value:', newQuantity);
        return; // Don't update if invalid
      }

      if (!this.quantityInput || !this.quantitySelector) {
        console.error('Quantity input or selector not found');
        return;
      }

      this.quantityInput.value = validQuantity;
      this.quantitySelector.currentQty = validQuantity;
      this.setAttribute('data-show-quantity-selector', validQuantity > 0 ? 'true' : 'false');
      this.setMaximumInputQuantity();
    }

    /**
     * Updates product quantity in cart with global lock to prevent 409 conflicts
     * @param {number} quantity - New quantity value
     */
    async updateQuantity(quantity) {
      // Handle empty or whitespace-only strings
      const trimmedQuantity = String(quantity).trim();

      // If empty, treat as 0 (remove from cart)
      if (trimmedQuantity === '') {
        quantity = 0;
      }

      // Validate quantity parameter
      const parsedQuantity = parseInt(quantity, 10);
      if (isNaN(parsedQuantity) || parsedQuantity < 0) {
        console.error('Invalid quantity parameter:', quantity);
        // Reset to previous value
        if (this.quantitySelector && this.quantityInput) {
          const currentQty =
            this.quantitySelector.currentQty || parseInt(this.quantityInput.dataset?.initialValue || '1', 10);
          this.quantityInput.value = currentQty;
        }
        return;
      }

      // If cart update in progress, queue this request
      if (ProductForm.cartUpdateInProgress) {
        console.log('Cart update in progress, queuing request');

        // Wait for current update to finish
        await new Promise((resolve) => {
          ProductForm.pendingUpdates.push(resolve);
        });
      }

      // Lock the cart for this update (prevents 409 errors)
      ProductForm.cartUpdateInProgress = true;

      if (!this.quantitySelector) {
        console.error('Quantity selector not found');
        this._releaseCartLock();
        return;
      }

      // this.quantitySelector.setAttribute('aria-disabled', 'true');

      const lineErrors = this.querySelector('.js-product-form-errors');
      if (lineErrors) {
        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      }

      // Validate required data - check multiple possible sources for variant ID
      let variantId = this.dataset.variantId;

      // If no variantId in dataset, try to get it from the form
      if (!variantId) {
        const idInput = this.form?.querySelector('[name="id"]');
        if (idInput) {
          variantId = idInput.value;
        }
      }

      if (!variantId) {
        console.error('Missing variant ID - dataset:', this.dataset, 'form:', this.form);
        if (lineErrors) {
          lineErrors.textContent = 'Unable to update cart: Missing product variant';
          lineErrors.hidden = false;
        }
        this.quantitySelector.removeAttribute('aria-disabled');
        this._releaseCartLock();
        return;
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      formData.append('sections_url', window.location.pathname);

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      };

      try {
        if (window.StorefrontCart?.isStorefrontApiEnabled?.()) {
          // Never assumeNewAdd: we're updating an existing line, need lineId from cache/fetch
          const result = await window.StorefrontCart.addOrUpdateCart(variantId, parsedQuantity, {
            assumeNewAdd: false,
          });
          if (result?.cart) {
            ProductForm.updateCartIcon({ storefrontCart: result.cart });
            if (this.cartDrawer) {
              this.cartDrawer.renderFromStorefrontCart?.(result.cart);
            }
            ProductForm.updateCartState(variantId, parsedQuantity);
          }
          return;
        }

        // Ajax API flow
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        const cartData = await cartResponse.json();
        const itemInCart = cartData.items.find((item) => item.variant_id.toString() === variantId.toString());

        let response;
        let data;

        if (itemInCart && parseInt(quantity, 10) === 0) {
          // Item exists and quantity is 0 - use updates endpoint to remove
          const updatesData = new FormData();
          updatesData.append('updates[' + variantId + ']', '0');
          updatesData.append('sections_url', window.location.pathname);
          updatesData.append('sections', sections);

          response = await fetch(theme.routes.cartUpdate, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: updatesData,
          });
          data = await response.json();
        } else if (itemInCart) {
          // Item exists - use change endpoint
          response = await fetch(theme.routes.cartChange, fetchRequestOpts);
          data = await response.json();
        } else {
          // Item not in cart - use add endpoint
          response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
          data = await response.json();
        }

        if (!response.ok) throw new Error(data.description || response.status);

        if (theme.settings.afterAtc !== 'page') {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);
          ProductForm.updateCartIcononQtyChange(data);
        } else {
          ProductForm.updateCartIcon(data);
        }

        if (this.cartDrawer) {
          this.cartDrawer.renderContents(data);
        }
      } catch (error) {
        console.error('Cart update error:', error, 'Variant ID:', variantId, 'Quantity:', quantity);

        if (lineErrors) {
          if (/^[0-9]+$/.test(error.message)) {
            lineErrors.textContent = theme.strings.cartError;
          } else if (error.message.includes('no valid id')) {
            lineErrors.textContent = 'Unable to update cart: Invalid product variant';
          } else {
            lineErrors.textContent = error.message;
          }
          lineErrors.hidden = false;
        }

        // Restore previous quantity on error
        const oldQuantity =
          this.quantitySelector.currentQty || parseInt(this.quantityInput?.dataset?.initialValue || '0', 10);
        if (oldQuantity >= 0) {
          this.updateQuantityState(oldQuantity);
        }
      } finally {
        // Only update if quantity is valid
        const finalQuantity = parseInt(quantity, 10);
        if (!isNaN(finalQuantity) && finalQuantity >= 0) {
          this.updateQuantityState(finalQuantity);
        }

        this.quantitySelector.removeAttribute('aria-disabled');

        // Release lock and process next queued request
        this._releaseCartLock();
      }
    }

    /**
     * Helper method to release the cart lock and process queued updates
     */
    _releaseCartLock() {
      ProductForm.cartUpdateInProgress = false;

      // Process next queued request if any
      if (ProductForm.pendingUpdates.length > 0) {
        const nextResolve = ProductForm.pendingUpdates.shift();
        nextResolve();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);

  document.addEventListener('on:cart:cleared', () => ProductForm.resetCartState());

  document.addEventListener('on:line-item:change', (evt) => {
    const { variantId, newQuantity } = evt.detail;
    if (variantId != null) ProductForm.updateCartState(variantId, newQuantity);
  });
}
